<script setup lang="ts">
type Size = 'xsmall' | 'small' | 'normal' | 'large';

type Props = {
  show: boolean;
  size?: Size;
  text?: String;
  iconLoader?: boolean;
  fullScreen?: boolean;
  inline?: boolean;
};

const {
  show,
  size = 'normal',
  text,
  iconLoader = false,
  fullScreen = true,
  inline = false,
} = defineProps<Props>();

const isLoaderVisible = computed<boolean>(() => {
  return !!show;
});

const sizeIconLoader = computed<`${string}px`>(() => {
  const sizeMap: Record<Size, `${string}px`> = {
    xsmall: '20px',
    small: '30px',
    normal: '40px',
    large: '50px',
  };

  return sizeMap[size] || '40px';
});
</script>

<template>
  <div
    v-if="isLoaderVisible"
    :class="[
      $style.loading,
      { [$style.inline]: inline },
      { [$style.fullScreen]: fullScreen },
    ]"
  >
    <template v-if="iconLoader">
      <BaseLoadingIconLoader
        duration="500ms"
        :show="true"
        :size="sizeIconLoader"
      />
    </template>

    <template v-else>
      <span :class="[$style.spinner, $style[size]]"></span>
      <span
        v-if="text"
        :class="$style.text"
      >
        {{ text }}
      </span>
    </template>
  </div>
</template>

<style module lang="scss">
.loading {
  @apply sm:rounded-lg opacity-90 flex items-center justify-center z-10 flex-col;

  &:not(.inline) {
    @apply absolute top-0 left-0 bg-bg-primary w-full h-full;
  }

  &.fullScreen {
    @apply fixed;
  }
}

.spinner {
  @apply inline-block rounded-full border-solid border-bg-primary border-r-primary border-b-primary;

  animation: rotate 600ms infinite linear;

  &.xsmall {
    @apply w-4 h-4 border-2;
  }

  &.small {
    @apply w-6 h-6 border-4;
  }

  &.normal {
    @apply w-9 h-9 border-[5px];
  }

  &.large {
    @apply w-14 h-14 border-8;
  }
}

.text {
  @apply text-texts text-xs font-semibold mt-4;
}

@keyframes rotate {
  to {
    transform: rotate(1turn);
  }
}
</style>
